import { useEffect, useState } from 'react';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';
import Tabs from '../../components/Tabs';
import { fetchSyncJobs, fetchNxtSyncStatus, runCampaignSync } from '../../services/sync';
import { format } from 'date-fns';

const syncModes = [
  {
    label: 'Most Recent',
    value: 'recent',
  },
  {
    label: 'Campaign ID',
    value: 'campaign_id',
  },
  {
    label: 'By Date',
    value: 'date',
  },
  {
    label: 'Full Sync',
    value: 'full',
  },
];

const NxtSyncStatusPanel = () => {
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const resp = await fetchNxtSyncStatus();
      if (resp.error) {
        setError(resp.error);
      } else {
        setSyncStatus(resp);
      }
    } catch (e) {
      setError('Failed to fetch NXT sync status');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <p className='text-center py-8'>Loading NXT sync status...</p>;
  }

  if (error) {
    return <p className='text-center py-8 text-red-500'>{error}</p>;
  }

  if (!syncStatus) {
    return <p className='text-center py-8'>No sync data available</p>;
  }

  return (
    <div className='flex flex-col gap-6'>
      <div className='rounded-sm border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark'>
        <div className='flex items-center justify-between'>
          <div>
            <span className='text-lg font-medium'>
              Authorize Sync Status
              {syncStatus.date_updated && (
                <span className='text-sm font-normal text-gray-500 ml-2'>
                  ({format(new Date(syncStatus.date_updated), 'MM/dd/yy h:mm a')})
                </span>
              )}
              :{' '}
            </span>
            {syncStatus.is_error ? (
              <span className='text-lg font-bold text-red-500'>
                {syncStatus.overall_status_display}
              </span>
            ) : (
              <span className='text-lg font-bold text-green-600'>
                {syncStatus.overall_status_display} — {syncStatus.message}
              </span>
            )}
          </div>
          <button
            onClick={loadStatus}
            className='inline-flex items-center gap-2 rounded-full border-[2px] border-stroke px-4 py-1.5 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700'
          >
            Refresh
          </button>
        </div>
      </div>

      {syncStatus.is_error && syncStatus.errors.length > 0 && (
        <div className='flex flex-col gap-4'>
          <p className='font-medium text-red-600'>
            All errors listed below must be fixed before synchronizing to MIP
          </p>
          <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
            <table className='datatable-table datatable-one w-full table-auto border-collapse overflow-hidden break-words px-4 md:table-fixed md:overflow-auto md:px-8'>
              <thead className='border-separate px-4'>
                <tr
                  className='border-t border-stroke bg-whiten'
                  style={{ fontSize: '14px' }}
                >
                  <th style={{ paddingTop: '16px', paddingBottom: '16px', width: '200px' }}>
                    Error Type
                  </th>
                  <th style={{ paddingTop: '16px', paddingBottom: '16px' }}>
                    Error Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {syncStatus.errors.map((err: any, i: number) => (
                  <tr key={i} className='border-t border-stroke'>
                    <td style={{ paddingTop: '16px', paddingBottom: '16px' }}>
                      <span className='font-medium'>{err.step_error}</span>
                    </td>
                    <td style={{ paddingTop: '16px', paddingBottom: '16px' }}>
                      <div>Cannot sync transaction: {err.detail}</div>
                      {err.description && (
                        <div className='text-sm text-gray-500 mt-1'>
                          {err.description}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      )}
    </div>
  );
};

const FundsRaisedSync = () => {
  const [loading, setLoading] = useState(true);
  const [lastSyncJobs, setLastSyncJobs] = useState([]);
  const [syncMode, setSyncMode] = useState('recent');
  const [startDate, setStartDate] = useState();
  const [syncing, setSyncing] = useState(false);
  const [campaignId, setCampaignId] = useState('');
  const [openTab, setOpenTab] = useState(1);

  useEffect(() => {
    getSyncJobs();
  }, []);

  const getSyncJobs = async () => {
    try {
      const resp = await fetchSyncJobs();
      console.log('resp: ', resp);
      setLastSyncJobs(resp);
    } catch (e) {
      console.log('e', e);
    }
  };

  const runSync = async () => {
    try {
      if (syncMode === 'date' && !startDate) {
        return;
      }
      if (syncMode === 'campaign_id' && !campaignId) {
        return;
      }
      setSyncing(true);
      const resp = await runCampaignSync(syncMode, startDate, campaignId);
      console.log('resp: ', resp);
      if (typeof resp === 'object') {
        if (resp.error) {
          console.log('error', resp);
        } else {
          if (resp.type === 'campaign_id') {
            // Campaign ID update
          } else {
            const newVal = resp.data;
            setLastSyncJobs([newVal, ...lastSyncJobs]);
          }
        }
      } else {
        console.log(resp);
      }
      setSyncing(false);
    } catch (e) {
      console.log('e', e);
      setSyncing(false);
    }
  };

  const handleDateSelect = (e) => {
    const val = e.target.value;
    console.log('val: ', val);
    setStartDate(val);
  };

  useEffect(() => {
    if (syncMode !== 'date') {
      setStartDate(null);
    }
    if (syncMode !== 'campaign_id') {
      setCampaignId('');
    }
  }, [syncMode]);

  return (
    <>
      <Breadcrumb pageName='Synchronize Data' />

      <Tabs
        tabNames={[{ name: 'LOC Sync' }, { name: 'NXT Sync Status' }]}
        openTab={openTab}
        setOpenTab={setOpenTab}
      >
        {openTab === 1 && (
          <>
            <div className='mb-6 flex flex-col gap-9'>
              <p className='uppercase'>
                Syncs the List of Contributors (LOC) based on the last sync ID of
                the hhlsync_loc table.
              </p>
              <div>
                <button
                  onClick={runSync}
                  disabled={syncing}
                  className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                >
                  {syncing ? `Syncing ...` : `Run Sync`}
                </button>
              </div>
            </div>

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
              <table className='datatable-table datatable-one w-full table-auto border-collapse overflow-hidden break-words px-4 md:table-fixed md:overflow-auto md:px-8'>
                <thead className='border-separate px-4'>
                  <tr
                    className='border-t border-stroke bg-whiten'
                    style={{ fontSize: '14px' }}
                  >
                    <th style={{ paddingTop: '16px', paddingBottom: '16px' }}>
                      Last Sync ID
                    </th>
                    <th style={{ paddingTop: '16px', paddingBottom: '16px' }}>
                      Last Sync Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lastSyncJobs.map((sync, i) => {
                    return (
                      <tr key={i} className='border-t border-stroke'>
                        <td style={{ paddingTop: '16px', paddingBottom: '16px' }}>
                          {sync.last_sync_id}
                        </td>
                        <td style={{ paddingTop: '16px', paddingBottom: '16px' }}>
                          {format(new Date(sync.last_sync_date), 'PPpp')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          </>
        )}

        {openTab === 2 && <NxtSyncStatusPanel />}
      </Tabs>
    </>
  );
};

export default FundsRaisedSync;
